import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getMe, LogOut, reset } from "../features/authSlice";
import { MdOutlineCancel } from "react-icons/md";
import { FaSignOutAlt, FaLeaf } from "react-icons/fa";
import { useStateContext } from "../contexts/ContextProvider";
import avatar from "../assets/avatar.jpg";

const UserProfile = () => {
  const [nama, setNama] = useState(null);
  const [urlImage, setUrlImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentMode, currentColor, setIsClicked, initialState } = useStateContext();

  // Colors
  const isDark = currentMode === 'Dark';
  const accentColor = currentColor || '#c9862a';
  const bgColor = isDark ? '#282C33' : '#ffffff';
  const borderColor = isDark ? '#3a3a34' : '#e8e0d0';
  const textPrimary = isDark ? '#f0ece3' : '#1a1a18';
  const textSecondary = isDark ? '#a09880' : '#6b6456';
  const bgSecondary = isDark ? '#242420' : '#f7f3ec';

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token && user) {
      fetchUserProfile();
    }
  }, [user]);

  // Fetch user profile based on role
  const fetchUserProfile = async () => {
    if (!user || user.role === 'admin') return;

    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const endpoint = user.role === "guru" ? "/profile-guru" : "/profile-siswa";

      const response = await axios.get(`${apiUrl}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.data?.url) {
        setUrlImage(response.data.data.url);
        setNama(response.data.data.nama);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClicked(initialState);
  };

  const handleLogout = async () => {
    try {
      await dispatch(LogOut());
      dispatch(reset());
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  // Role display text
  const getRoleDisplay = (role) => {
    const roleText = {
      admin: 'Administrator',
      guru: 'Guru Green Science',
      siswa: 'Siswa Green Science'
    };
    return roleText[role] || role;
  };

  return (
    <div
      className="rounded-xl w-80 shadow-2xl z-[9999] relative border overflow-hidden"
      style={{
        backgroundColor: bgColor,
        borderColor: borderColor,
      }}
    >
      {/* Header */}
      <div
        className="p-4 border-b"
        style={{
          backgroundColor: isDark ? '#1a1a18' : `${accentColor}08`,
          borderColor: borderColor,
        }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: accentColor }}
            >
              <FaLeaf className="text-white text-sm" />
            </div>
            <div>
              <h3
                className="font-bold"
                style={{ color: textPrimary }}
              >
                Profil
              </h3>
              <p
                className="text-xs"
                style={{ color: textSecondary }}
              >
                Kelola akun Anda
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg transition-all duration-300"
            style={{
              color: textSecondary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = bgSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <MdOutlineCancel className="text-lg" />
          </button>
        </div>
      </div>

      {/* User Info Section */}
      <div className="p-4">
        <div
          className="flex gap-4 items-center p-4 rounded-lg border mb-4"
          style={{
            backgroundColor: bgSecondary,
            borderColor: borderColor,
          }}
        >
          {/* Avatar */}
          <div className="relative">
            {loading ? (
              <div
                className="w-14 h-14 rounded-full animate-pulse"
                style={{ backgroundColor: `${accentColor}40` }}
              />
            ) : (
              <img
                className="w-14 h-14 rounded-full object-cover border-2"
                style={{ borderColor: accentColor }}
                src={urlImage && urlImage !== "http://localhost:5000/images/null" ? urlImage : avatar}
                alt="user-profile"
                onError={(e) => {
                  e.target.src = avatar;
                }}
              />
            )}
          </div>

          {/* User Details */}
          <div className="flex-1 min-w-0">
            <h4
              className="font-bold truncate"
              style={{ color: textPrimary }}
            >
              {nama ? capitalizeFirstLetter(nama) : capitalizeFirstLetter(user?.username || "Admin")}
            </h4>
            <p
              className="text-sm truncate"
              style={{ color: textSecondary }}
            >
              {user?.email || "admin@rumohpanganaceh.id"}
            </p>
            <span
              className="text-xs px-3 py-1 rounded-full mt-1.5 inline-block font-semibold"
              style={{
                backgroundColor: `${accentColor}30`,
                color: accentColor
              }}
            >
              {getRoleDisplay(user?.role || 'admin')}
            </span>
          </div>
        </div>

        {/* Quick Info */}
        <div
          className="p-3 rounded-lg mb-4"
          style={{
            backgroundColor: isDark ? '#1a1a18' : `${accentColor}10`,
            border: `1px solid ${isDark ? '#2e2e28' : `${accentColor}30`}`,
          }}
        >
          <div className="flex items-center gap-2">
            <FaLeaf style={{ color: accentColor }} className="text-sm" />
            <span
              className="text-xs font-medium"
              style={{ color: textSecondary }}
            >
              Rumoh Pangan Aceh
            </span>
          </div>
          <p
            className="text-[10px] mt-1"
            style={{ color: textSecondary }}
          >
            Organisasi Ketahanan Pangan Rakyat
          </p>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 font-medium py-3 px-4 rounded-lg transition-all duration-300 hover:scale-[1.02]"
          style={{
            backgroundColor: '#dc2626',
            color: 'white',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#b91c1c';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
          }}
        >
          <FaSignOutAlt />
          <span>Keluar</span>
        </button>
      </div>
    </div>
  );
};

export default UserProfile;